import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable, FunctionsError } from 'firebase/functions';
import type { StoryRunDoc } from '@spirits/shared';
import { functions } from '../firebase';
import { RecapView } from './RecapView';

const resetStoryCallable = httpsCallable<
  { storyId: string },
  { ok: boolean; storyId: string; currentSceneId: string }
>(functions, 'resetStory');

// Center-panel view for a finished run: the recap (divergence beats) plus
// play-again / start-another controls. Lives as the "Ending" page in the feed so
// the storyline + spirits' channel stay docked around it (mirrors ScenePage).
export function RecapPage({
  story,
  onClose,
}: {
  story: StoryRunDoc & { id: string };
  onClose: () => void;
}) {
  const [restarting, setRestarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const endingTitle = useMemo(
    () => story.endings?.find((e) => e.id === story.endingId)?.title,
    [story.endings, story.endingId],
  );
  const recap = story.recap;

  async function restart() {
    if (restarting) return;
    setRestarting(true);
    setError(null);
    try {
      await resetStoryCallable({ storyId: story.id });
      // The story doc flips back to active; SceneStage re-selects the opening.
    } catch (e) {
      setError((e as FunctionsError)?.message ?? 'could not restart');
      setRestarting(false);
    }
  }

  return (
    <div className="h-full flex flex-col border-l border-slate-900 bg-slate-950 min-h-0">
      <div className="flex-none border-b border-slate-800 p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden text-slate-400 hover:text-slate-200 -ml-1 px-1 text-xl leading-none"
            title="back"
          >
            ←
          </button>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">The story is told ✦</p>
            <h2 className="text-2xl text-slate-100 truncate">{recap?.title ?? story.title}</h2>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="hidden lg:block text-slate-500 hover:text-slate-300 text-lg leading-none flex-none"
          title="close"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6 min-h-0">
        <div className="max-w-2xl mx-auto">
          {recap ? (
            <RecapView
              title={recap.title}
              blurb={recap.blurb}
              beats={recap.beats}
              closing={recap.closing}
              storyTitle={story.title}
              category={story.category}
              endingTitle={endingTitle}
            />
          ) : (
            <p className="text-slate-400 text-center">The telling is being written…</p>
          )}

          {error && <p className="mt-6 text-center text-xs text-rose-400">{error}</p>}

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={restart}
              disabled={restarting}
              className="rounded-lg border border-slate-700 text-slate-300 hover:text-slate-100 hover:border-slate-500 px-4 py-2 text-sm disabled:opacity-50"
            >
              {restarting ? 'restarting…' : '↺ Play this story again'}
            </button>
            <Link
              to="/"
              className="rounded-lg border border-amber-400/50 bg-amber-950/30 text-amber-100 hover:bg-amber-900/40 px-4 py-2 text-sm"
            >
              Start another story →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
